import { queryOptions, useQuery } from '@tanstack/react-query';

import {
  TimeRange,
  ApiResponse,
  QueryOptions,
  QueryRefetchInterval,
} from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const apiFn = apiClient.dbo.getCachingSummary;

type CachingSummaryQueryData = ApiResponse<typeof apiFn>['summary'];

type UseCachingSummaryQueryArgs<SELECT> = QueryOptions<
  CachingSummaryQueryData,
  SELECT
> & {
  cacheGroupId?: string;
  cacheId?: string;
  username?: string;
  key: string;
  getRange: () => TimeRange;
};

const DBO_CACHING_SUMMARY_QUERY_KEY = 'dbo-caching-summary';

const getCachingSummaryQueryKey = ({
  cacheGroupId,
  cacheId,
  username,
  key,
}: {
  cacheGroupId?: string;
  cacheId?: string;
  username?: string;
  key?: string;
} = {}) =>
  [
    DBO_CACHING_SUMMARY_QUERY_KEY,
    cacheGroupId,
    cacheId,
    username,
    key,
  ] as const;

const getCachingSummaryQueryOptions = <SELECT = CachingSummaryQueryData>({
  cacheGroupId,
  cacheId,
  username,
  enabled,
  key,
  getRange,
  refetchInterval,
}: {
  cacheGroupId?: string;
  cacheId?: string;
  username?: string;
  key: string;
  getRange: () => TimeRange;
  enabled?: boolean;
  refetchInterval?: QueryRefetchInterval<CachingSummaryQueryData, SELECT>;
}) => {
  const normalizedUsername = username || undefined;
  return queryOptions({
    queryKey: getCachingSummaryQueryKey({
      cacheGroupId,
      cacheId,
      username: normalizedUsername,
      key,
    }),
    queryFn: async () => {
      const { from, to } = getRange();
      const { data } = await apiFn({
        groupId: cacheGroupId!,
        cacheId: cacheId!,
        username: normalizedUsername,
        startTime: from,
        endTime: to,
      });
      return data.summary;
    },
    enabled: makeIsEnabled(enabled, !!cacheGroupId && !!cacheId),
    refetchInterval,
  });
};

export const useCachingSummaryQuery = <SELECT = CachingSummaryQueryData>({
  cacheGroupId,
  cacheId,
  username,
  enabled,
  key,
  getRange,
  refetchInterval,
}: UseCachingSummaryQueryArgs<SELECT>) => {
  return useQuery(
    getCachingSummaryQueryOptions({
      cacheGroupId,
      cacheId,
      username,
      key,
      getRange,
      enabled,
      refetchInterval,
    })
  );
};
